import {
    Colors,
    FieldsetWrapper,
    Icon,
    MultilineInput,
    type MultilineInputProps,
} from '@amelio/ui-library';
import { useState } from 'react';

interface DraggableOptionProps {
    value: string;
    onChange: (value: string) => void;
    onRemove: () => void;
    onAdd?: () => void;
    canAddNewOption: boolean;
    className?: string;
    disabled?: boolean;

    /** We only show the "Add Option" and red "Remove" icons for the last item in the list. */
    isLastOption?: boolean;
    canRemove?: boolean;
    error?: boolean;
    dataTestId: string;
    inputSize?: MultilineInputProps['size'];
    inputWidth?: string;
    label?: string;
}

const QuestionDraggableOption = ({
    value,
    onChange,
    onRemove,
    onAdd,
    canAddNewOption = true,
    className = '',
    disabled = false,
    isLastOption = true,
    canRemove = true,
    error,
    dataTestId,
    inputSize = 'md',
    inputWidth,
    label,
}: DraggableOptionProps) => {
    const [isFocused, setIsFocused] = useState(false);

    const inputContent = (
        <MultilineInput
            placeholder={isFocused || value.length > 0 ? '' : 'Option'}
            value={value}
            onChange={onChange}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            disabled={disabled}
            error={error}
            dataTestId={`${dataTestId}-input`}
            size={inputSize}
            rows={1}
            autoGrow={true}
            borderColor={label ? 'transparent' : undefined}
            className={`!${inputWidth} md:min-w-[300px] ${
                !label
                    ? 'hover:border-primary focus-within:border-primary border border-transparent'
                    : ''
            }`}
        />
    );

    return (
        <div className={`flex items-center gap-4 ${className} md:min-w-[300px]`}>
            <FieldsetWrapper
                label={label}
                legendColor={Colors.BASE_500}
                hasValue={value.length > 0}
                isFocused={isFocused}
            >
                {inputContent}
            </FieldsetWrapper>

            <button
                type={'button'}
                onClick={onRemove}
                className={'cursor-pointer disabled:cursor-not-allowed disabled:opacity-50'}
                aria-label={'Remove option'}
                data-testid={`${dataTestId}-remove`}
                disabled={disabled || !canRemove}
            >
                <Icon
                    name={'essential-close-circle'}
                    className={'h-2 w-2'}
                    color={!canRemove || !isLastOption ? Colors.BASE : Colors.ERROR_800}
                />
            </button>

            {canAddNewOption && isLastOption && (
                <button
                    type={'button'}
                    onClick={onAdd}
                    className={'cursor-pointer disabled:cursor-not-allowed'}
                    aria-label={'Add option'}
                    data-testid={`${dataTestId}-add`}
                    disabled={disabled}
                >
                    <Icon
                        name={'essential-add-circle'}
                        className={'h-2 w-2'}
                        color={Colors.BASE}
                    />
                </button>
            )}
        </div>
    );
};

export default QuestionDraggableOption;
